import { MdFileDownload } from 'react-icons/md';
import { Button, Modal } from 'rsuite';
import { CompetitorProduct, MonitoringItem } from '../types';
import styles from './ModalDetalhamento.module.scss';

const MAX_VISIBLE_PRODUCTS = 10;

type ProductsFieldProps = {
  label: string;
  products: CompetitorProduct[];
  onDownload: () => void;
};

const ProductsField = ({ label, products, onDownload }: ProductsFieldProps) => {
  const totalProducts = products.length;
  const hasMoreProducts = totalProducts > MAX_VISIBLE_PRODUCTS;
  const visibleProducts = hasMoreProducts ? products.slice(0, MAX_VISIBLE_PRODUCTS) : products;
  const remainingCount = totalProducts - MAX_VISIBLE_PRODUCTS;

  return (
    <div className={styles.field}>
      <span className={styles.label}>{label}</span>

      {visibleProducts.length > 0 && (
        <div className={styles.productsList}>
          {visibleProducts.map(product => (
            <p key={product.ean} className={styles.productItem}>
              {product.ean} - {product.name}
            </p>
          ))}
          {hasMoreProducts && <p className={styles.moreProducts}>mais {remainingCount} produtos</p>}
        </div>
      )}

      {hasMoreProducts && (
        <button type="button" className={styles.downloadLink} onClick={onDownload}>
          <MdFileDownload size={16} />
          Baixe a lista completa de produtos
        </button>
      )}
    </div>
  );
};

type NamesFieldProps = {
  label: string;
  names: string[];
};

const NamesField = ({ label, names }: NamesFieldProps) => (
  <div className={styles.field}>
    <span className={styles.label}>{label}</span>

    {names.length > 0 && (
      <div className={styles.productsList}>
        {names.map(name => (
          <p key={name} className={styles.productItem}>
            {name}
          </p>
        ))}
      </div>
    )}
  </div>
);

const PRODUTOS_FIELD_LABEL_POR_TIPO: Record<'PRODUTO' | 'CATEGORIA' | 'SECAO', string> = {
  PRODUTO: 'PRODUTOS',
  CATEGORIA: 'CATEGORIAS',
  SECAO: 'SEÇÕES',
};

type ModalDetalhamentoProps = {
  show: boolean;
  onClose: () => void;
  item: MonitoringItem | null;
  onDownloadProducts?: (item: MonitoringItem) => void;
};

export const ModalDetalhamento = ({ show, onClose, item, onDownloadProducts }: ModalDetalhamentoProps) => {
  if (!item) return null;

  const handleDownloadClick = () => {
    onDownloadProducts?.(item);
  };

  return (
    <Modal className={styles.modal} size="sm" show={show} onHide={onClose}>
      <Modal.Header className={styles.header}>
        <Modal.Title className={styles.title}>Detalhamento</Modal.Title>
        <p className={styles.subtitle}>Confira os detalhes selecionados para esse monitoramento</p>
      </Modal.Header>

      <Modal.Body className={styles.body}>
        <div className={styles.section}>
          {item.isVarejo ? (
            item.details.tipoComparacao === 'CATEGORIA' ? (
              <NamesField label={PRODUTOS_FIELD_LABEL_POR_TIPO.CATEGORIA} names={item.details.categorias} />
            ) : item.details.tipoComparacao === 'SECAO' ? (
              <NamesField label={PRODUTOS_FIELD_LABEL_POR_TIPO.SECAO} names={item.details.secoes} />
            ) : (
              <ProductsField label={PRODUTOS_FIELD_LABEL_POR_TIPO.PRODUTO} products={item.details.allProducts} onDownload={handleDownloadClick} />
            )
          ) : (
            <>
              <div className={styles.field}>
                <span className={styles.label}>PRODUTO REFERÊNCIA</span>
                <p className={styles.value}>{item.details.productName}</p>
              </div>
              <ProductsField label="PRODUTOS COMPETIDORES" products={item.details.competitorProducts} onDownload={handleDownloadClick} />
            </>
          )}
        </div>

        <div className={styles.section}>
          <div className={styles.field}>
            <span className={styles.label}>REDES</span>
            <p className={styles.value}>{item.details.networksCount} redes selecionadas</p>
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.field}>
            <span className={styles.label}>TIPO DE LOCAL</span>
            <p className={styles.value}>{item.details.tipoLocal}</p>
          </div>

          <div className={styles.field}>
            <span className={styles.label}>{item.details.tipoLocal === 'UF' ? 'UFS' : 'CIDADES'}</span>
            <p className={styles.value}>{item.details.cities.length > 0 ? item.details.cities.join(', ') : '-'}</p>
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.field}>
            <span className={styles.label}>PERÍODO</span>
            <p className={styles.value}>{item.details.period}</p>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className={styles.footer}>
        <Button className={styles.closeButton} appearance="primary" onClick={onClose}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
